import { useQuery } from '@tanstack/react-query';
import { fetchItems } from '../support/queries';
import type { DateWindow } from '../support/campaignLeader';
import type { DeliveriesRollupResponse, EnrollmentRollupResponse } from '../support/campaignRollup';
import type { JourneyMapRollupResponse, ParticipationRollupResponse } from '../support/campaignParticipation';

interface RollupArgs {
  /** The leader whose org subtree to roll up (for an individual, their own email). */
  manager: string;
  /** One campaign, or undefined for all campaigns. */
  campaignId?: number;
  enabled?: boolean;
}

const base = (manager: string, campaignId?: number) => ({
  manager,
  ...(campaignId != null ? { campaign: campaignId } : {}),
});

/**
 * `db/campaign/deliveries-rollup/`: delivery counts by status, pipeline $ and per-owner figures over the
 * manager's org subtree, computed server-side (no row cap). `window` is an inclusive fiscal date window,
 * sent as `from`/`to`; the server echoes it back, which callers check before trusting the figures.
 */
export function useCampaignDeliveriesRollup({
  manager,
  campaignId,
  window,
  enabled = true,
}: RollupArgs & { window?: DateWindow | null }) {
  const target = String(manager || '').trim().toLowerCase();
  return useQuery<DeliveriesRollupResponse>({
    queryKey: ['CampaignDeliveriesRollup', target, campaignId ?? 'all', window?.start ?? null, window?.end ?? null],
    queryFn: () =>
      fetchItems(
        'db/campaign/deliveries-rollup/',
        { ...base(target, campaignId), ...(window ? { from: window.start, to: window.end } : {}) },
        { includePageSize: false },
      ),
    enabled: !!target && enabled, retry: false, refetchOnWindowFocus: false, staleTime: 30_000,
  });
}

/**
 * `db/campaign/enrollment-rollup/`: enrollment counts by status, mean completion and per-user figures over
 * the manager's org subtree. Enrollment progress is deliberately not period-filtered.
 */
export function useCampaignEnrollmentRollup({ manager, campaignId, enabled = true }: RollupArgs) {
  const target = String(manager || '').trim().toLowerCase();
  return useQuery<EnrollmentRollupResponse>({
    queryKey: ['CampaignEnrollmentRollup', target, campaignId ?? 'all'],
    queryFn: () => fetchItems('db/campaign/enrollment-rollup/', base(target, campaignId), { includePageSize: false }),
    enabled: !!target && enabled, retry: false, refetchOnWindowFocus: false, staleTime: 30_000,
  });
}

/**
 * `db/campaign/participation-rollup/`: per-person participation facts (enrolled, journey-map counts,
 * deliveries) for everyone in the manager's org who has any activity. The campaign filter and the fiscal
 * window apply to enrollments and deliveries; enablement counts the maps configured on the campaign(s) and is
 * not period-filtered.
 */
export function useCampaignParticipationRollup({
  manager,
  campaignId,
  window,
  enabled = true,
}: RollupArgs & { window?: DateWindow | null }) {
  const target = String(manager || '').trim().toLowerCase();
  return useQuery<ParticipationRollupResponse>({
    queryKey: ['CampaignParticipationRollup', target, campaignId ?? 'all', window?.start ?? null, window?.end ?? null],
    queryFn: () =>
      fetchItems(
        'db/campaign/participation-rollup/',
        { ...base(target, campaignId), ...(window ? { from: window.start, to: window.end } : {}) },
        { includePageSize: false },
      ),
    enabled: !!target && enabled, retry: false, refetchOnWindowFocus: false, staleTime: 30_000,
  });
}

/**
 * `db/journey-map/map-rollup/`: progression of the journey maps configured on a campaign's enablement steps
 * (assigned, not started, in progress, completed, mean progress and days to complete, out-of-sync) over the
 * manager's org. One campaign, or every campaign's maps when `campaignId` is undefined.
 */
export function useJourneyMapRollup({
  manager,
  campaignId,
  enabled = true,
}: {
  manager: string;
  campaignId?: number;
  enabled?: boolean;
}) {
  const target = String(manager || '').trim().toLowerCase();
  return useQuery<JourneyMapRollupResponse>({
    queryKey: ['JourneyMapRollup', target, campaignId ?? 'all'],
    queryFn: () => fetchItems('db/journey-map/map-rollup/', base(target, campaignId), { includePageSize: false }),
    enabled: !!target && enabled, retry: false, refetchOnWindowFocus: false, staleTime: 30_000,
  });
}
